import { FC, ReactNode, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { trackEvent } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';
import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { getPriceFromAmountListByCurrency } from 'dibs-buyer-price/exports/getPriceFromAmountListByCurrency';

import { useUserShippingRegionsUrlLabels } from './hooks/useUserShippingRegionsUrlLabels';

import { PdpSharedLowerShippingInfo_item$key } from './__generated__/PdpSharedLowerShippingInfo_item.graphql';
import { PdpSharedLowerShippingInfo_viewer$key } from './__generated__/PdpSharedLowerShippingInfo_viewer.graphql';

import styles from './PdpSharedLowerShippingInfo.scss';

const viewerFragment = graphql`
    fragment PdpSharedLowerShippingInfo_viewer on Viewer
    @argumentDefinitions(
        fetchRegionalInfo: { type: "Boolean", defaultValue: false }
        userZipCode: { type: "String", defaultValue: "" }
        userCountryCode: { type: "String", defaultValue: "" }
    ) {
        ...useShippingPrice_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

const itemFragment = graphql`
    fragment PdpSharedLowerShippingInfo_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        buyerId: { type: "String", defaultValue: "" }
        fetchShippingCostInfo: { type: "Boolean", defaultValue: false }
        itemQuantity: { type: "Int" }
        shippingRegions: { type: "[String]", defaultValue: "[]" }
        userCountryCode: { type: "String", defaultValue: "" }
    ) {
        serviceId
        #Ensure that arguments witch affect total results count are passed to field used in
        #app-buyer-misc/src/visuallySimilar/components/VisuallySimilarLayout.jsx aswell.
        #This is needed because we don't want to generate the link if we don't have any results TOF-175
        moreLikeThis(first: 0, shippingRegions: $shippingRegions) {
            totalResults
        }
        displayPrice(isTrade: $isTrade, buyerId: $buyerId, userCountryCode: $userCountryCode) {
            convertedAmountList {
                currency
                amount
            }
        }
        ...useShippingPrice_item
            @arguments(
                buyerId: $buyerId
                fetchShippingCostInfo: $fetchShippingCostInfo
                itemQuantity: $itemQuantity
            )
    }
`;

const SHIPPING_COST_PERCENTAGE_THRESHOLD = 0.25;

export const PdpSharedLowerShippingInfo: FC<{
    viewer: PdpSharedLowerShippingInfo_viewer$key;
    item: PdpSharedLowerShippingInfo_item$key | null;
    currency: string;
}> = ({ viewer: viewerRef, item: itemRef, currency }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const userRegionsUrlLabels = useUserShippingRegionsUrlLabels();
    const shippingPrice =
        useShippingPrice({
            viewer,
            item,
            currency,
            type: FREE_SHIPPING_AND_COST,
        }) || 0;
    const itemPrice =
        getPriceFromAmountListByCurrency(
            item?.displayPrice?.[0]?.convertedAmountList || null, //TODO handle multi sku
            currency
        ) || 0;

    const totalPrice = itemPrice + shippingPrice;

    const itemId = item?.serviceId;
    const moreLikeThisTotalResults = item?.moreLikeThis?.totalResults;

    const shopNowLink = useCallback(
        (chunks: ReactNode) => (
            <Link
                className={styles.showNowLink}
                href={`/similar/?item=${itemId}&shipping-regions=${userRegionsUrlLabels.join(',')}`}
                onClick={event => {
                    trackEvent(
                        {
                            category: 'product',
                            action: 'ship now click',
                            label: 'pdp',
                        },
                        event
                    );
                }}
            >
                {chunks}
            </Link>
        ),
        [itemId, userRegionsUrlLabels]
    );

    if (
        !userRegionsUrlLabels.length ||
        !moreLikeThisTotalResults ||
        //check if totalPrice exists to prevent division by zero
        !totalPrice ||
        //if shipping cost is below 25% of total cost don't show this treatment
        shippingPrice / totalPrice < SHIPPING_COST_PERCENTAGE_THRESHOLD
    ) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <FormattedMessage
                id="PdpSharedLowerShippingInfo.text"
                defaultMessage="Browse similar items with lower shipping or local pickup. <a>Shop Now</a>"
                values={{
                    a: shopNowLink,
                }}
            />
        </div>
    );
};
